// `Fountain.build` (FountainBuilder.swift): writes script elements as Fountain — plain Fountain wherever it reads back
// as the same element, and a forcing character (`.` `@` `>` `!` `~`) only where it wouldn't: a heading without
// INT./EXT., a cue that isn't all capitals, a transition not ending TO:, action that looks like a cue or heading.
import type { FountainElement } from './contract.ts';
import { isCharacterCue, isSceneHeading } from './fountain.ts';

/** `Fountain.Written`: one element to write, its text with Fountain emphasis already in it. */
export interface Written {
  element: FountainElement | 'boneyard';
  text: string;
  /** A character cue whose speech sits beside the previous one. */
  dual?: boolean;
  /** A scene heading's number. */
  sceneNumber?: string;
  /** A section's depth (1 = act). */
  level?: number;
}

const isUppercase = (s: string) => /\p{L}/u.test(s) && s === s.toUpperCase();
const SPEECH = new Set<string>(['character', 'parenthetical', 'dialogue']);

export function buildFountain(paragraphs: Written[], titlePage: { key: string; value: string }[] = []): string {
  const blocks: string[] = [];
  if (titlePage.length) {
    blocks.push(titlePage.map(({ key, value }) => {
      const lines = value.split('\n');
      return lines.length === 1 ? `${key}: ${value}` : `${key}:\n` + lines.map((l) => `   ${l}`).join('\n');
    }).join('\n'));
  }
  let previous: string | null = null;
  for (const p of paragraphs) {
    const text = p.text.trim();
    if (!text && p.element !== 'pageBreak') continue;
    let line: string;
    switch (p.element) {
      case 'sceneHeading': {
        const heading = text.replaceAll('\n', ' ');
        line = (isSceneHeading(heading) ? heading : '.' + heading) + (p.sceneNumber ? ` #${p.sceneNumber}#` : '');
        break;
      }
      case 'character': {
        const cue = text.replaceAll('\n', ' ');
        line = (isCharacterCue(cue) ? cue : '@' + cue) + (p.dual ? ' ^' : '');
        break;
      }
      case 'parenthetical': line = text.startsWith('(') ? text : `(${text})`; break;
      case 'dialogue': line = text; break;
      case 'transition': line = isUppercase(text) && text.endsWith('TO:') ? text : '> ' + text; break;
      case 'centered': line = `> ${text} <`; break;
      case 'lyric': line = text.split('\n').filter((l) => l !== '').map((l) => '~' + l).join('\n'); break;
      case 'section': line = '#'.repeat(Math.max(1, p.level ?? 1)) + ' ' + text; break;
      case 'synopsis': line = '= ' + text; break;
      case 'note': line = `[[${text}]]`; break;
      case 'pageBreak': line = '==='; break;
      case 'boneyard': line = `/*\n${text}\n*/`; break;
      default: line = action(text);
    }
    // Speech continues the cue's block; everything else is its own block.
    if ((p.element === 'parenthetical' || p.element === 'dialogue') && previous && SPEECH.has(previous) && blocks.length) {
      blocks[blocks.length - 1] += '\n' + line;
    } else {
      blocks.push(line);
    }
    previous = p.element;
  }
  return blocks.join('\n\n');
}

/** Action, forced with `!` where Fountain would read it as something else: its first line (and any after a blank
 *  line) as a heading, cue, transition or markup; any other line as markup (`.45 rounds` would be a forced heading,
 *  `@`, `>`, `~`, `=`, `#`, `[[…]]` and `/*` their elements). */
function action(text: string): string {
  const lines = text.split('\n');
  let previousBlank = true;
  return lines.map((line, n) => {
    const wasBlank = previousBlank;
    previousBlank = line.trim() === '';
    const trimmed = line.trim();
    if (!trimmed) return line;
    const markup = '.@>~=#!'.includes(trimmed[0]) || trimmed.startsWith('/*') || (trimmed.startsWith('[[') && trimmed.endsWith(']]'));
    if (markup) return '!' + line;
    if (!wasBlank) return line;
    const moreFollows = lines.slice(n + 1).some((l) => l.trim() !== '') && lines[n + 1]?.trim() !== '';
    const looksLikeCue = moreFollows && isCharacterCue(trimmed);
    const looksLikeTransition = isUppercase(trimmed) && trimmed.endsWith('TO:');
    return isSceneHeading(trimmed) || looksLikeCue || looksLikeTransition ? '!' + line : line;
  }).join('\n');
}
